import { useState } from "react";
import to from "await-to-js";
import cn from "clsx";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import JDIPrice from "@/common-components-src/js/price/index";
import UKToast from "@/common-components-src/components/UKToast/index";
import UKImg from "@/common-components-src/components/UKImg";
import UKPageLoading from "@/common-components-src/components/UKPageLoading";
import WarningIcon from "@/app/csr-src/assets/icons/WarningIcon.svg";
import VectorDown from "@/app/csr-src/assets/icons/VectorDown.svg";
import { useStores } from "@/app/csr-src/models";
import RepayOrderModal from "@/app/csr-src/pages/checkout/components/repayOrderModal";
import EmptyPage from "../emptyPage";
import ErrorPage from "../errorPage";
import CommonButton from "../commonButton";
import CountDown from "../countDown";
import ConfirmModal from "../confirmModal";
import Pagination from "../pagination";
import TrackModal from "../trackModal";
import InvoiceModal from "../invoiceModal";
import RefundModal from "../refundModal";
import AgeTipsModal from "../ageTipsModal";
import ProductTag from "../productTag";
import styles from "./index.module.scss";
import i18next from "i18next";
import { delHistoryOrder, cancelOrder } from "@/app/csr-src/apis/order/order.api";
import {
  ICancelOrderReq,
  IOrderDetailReq,
  ButtonsVo,
  OrderBaseInfoVo,
  WareInfoListVo,
  PriceInfoItemVo,
  AfterSaleProgressVo,
  OrderExtInfoVo,
  LogisticsInfoVo,
  InvoiceListVo,
  RefundInfoVo,
  TagContentVo,
} from "@/app/csr-src/apis/order/order.interface";

// 埋点
import { EXPOSURE_RECORD, sendClickTrack } from "@/tracks/25335";

interface orderItemsProps {
  updateNumber: () => void;
}

const orderItems: React.FC<orderItemsProps> = observer(({ updateNumber }) => {
  const { t } = useTranslation();
  const { orderStore } = useStores();
  const orderList = orderStore?.orderListInfo?.orderInfoDTOS || [];
  const [openStates, setOpenStates] = useState<{ [key: string]: boolean }>({});
  const navigate = useNavigate();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showTrackModal, setShowTrackModal] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showAgeTipsModal, setShowAgeTipsModal] = useState(false);

  const [curOrder, setCurOrder] = useState("");
  const [curOrderItem, setCurOrderItem] = useState<{
    buttons: ButtonsVo[];
    orderBaseInfo: OrderBaseInfoVo;
    wareInfoList: WareInfoListVo[];
    priceInfoItemDTO: PriceInfoItemVo;
    afterSaleProgressDTO: AfterSaleProgressVo;
    orderExtInfoDTO: OrderExtInfoVo;
    logisticsInfoDTO: LogisticsInfoVo;
    invoiceItemDTO: InvoiceListVo[];
    orderTagContentVO: TagContentVo;
  } | null>(null);
  const [curOrderIndex, setCurOrderIndex] = useState(0);

  const currentPage = orderStore.pageNum || 1;

  if (orderStore.loading) {
    return <UKPageLoading style={{ height: "313px" }} text="Loading" />;
  }

  // 埋点需要的数据
  const orderTabList = [
    {
      name: t("order.orderList.orderTabs.allTab"),
      state: "4096",
    },
    {
      name: t("order.orderList.orderTabs.unpaidTab"),
      state: "1",
    },
    {
      name: t("order.orderList.orderTabs.pendingReceiptTab"),
      state: "128",
    },
    {
      name: t("order.orderList.orderTabs.completedTab"),
      state: "1024",
    },
    {
      name: t("order.orderList.orderTabs.cancelledTab"),
      state: "-1",
    },
  ];
  const tabName = orderTabList.find((item) => item?.state === orderStore.orderType)?.name || "All";

  // 跳转到订单详情
  const jumpOrderDetail = (
    index: number,
    orderItem: {
      orderBaseInfo: OrderBaseInfoVo;
      wareInfoList: WareInfoListVo[];
      priceInfoItemDTO: PriceInfoItemVo;
      afterSaleProgressDTO: AfterSaleProgressVo;
      orderExtInfoDTO: OrderExtInfoVo;
      logisticsInfoDTO: LogisticsInfoVo;
      refundInfoDTO: RefundInfoVo;
      buttons: ButtonsVo[];
      invoiceItemDTO: InvoiceListVo[];
    },
  ) => {
    const scrollY = window.scrollY || document.documentElement.scrollTop;
    const { orderBaseInfo, logisticsInfoDTO } = orderItem || {};
    const { orderId = "", orderStatusId = "" } = orderBaseInfo;
    const packageNum = `${(logisticsInfoDTO?.shippedPackageNum || 0) + (logisticsInfoDTO?.deliveredPackageNum || 0)}`;

    // 埋点
    sendClickTrack.OrderList_OrderDetail({
      orderId,
      orderStatus: orderStatusId as "1" | "3" | "8" | "6" | "5",
      tab_name: tabName,
      index,
      page: currentPage,
      packageNum,
    });

    const orderInfo = {
      scrollY,
      pageNum: currentPage,
      orderType: orderStore.orderType || "4096",
    };
    sessionStorage.setItem("orderInfo", JSON.stringify(orderInfo));
    navigate("/profile/my-orders/order-details?orderId=" + orderId);
  };

  // 展开与收起功能
  const toggleStatus = (event: React.MouseEvent<HTMLDivElement>, orderId: string) => {
    event.stopPropagation();
    setOpenStates((prevState) => ({
      ...prevState,
      [orderId]: !prevState[orderId],
    }));
  };

  // 订单配送、待付款等信息展示
  const orderInfoRender = (item: {
    buttons: ButtonsVo[];
    orderBaseInfo: OrderBaseInfoVo;
    wareInfoList: WareInfoListVo[];
    priceInfoItemDTO: PriceInfoItemVo;
    afterSaleProgressDTO: AfterSaleProgressVo;
    orderExtInfoDTO: OrderExtInfoVo;
    logisticsInfoDTO: LogisticsInfoVo;
    refundInfoDTO: RefundInfoVo;
    invoiceItemDTO: InvoiceListVo[];
    orderTagContentVO: TagContentVo;
  }) => {
    const type = item?.orderBaseInfo.orderStatusId || "";
    const leftTime = item?.orderBaseInfo?.remainingPaymentTime || 0;
    const orderId = item.orderBaseInfo?.orderId || "";
    // JDISVOrderListOrderStatusTypeWaitingPay = 1, //待支付、等待付款确认
    // JDISVOrderListOrderStatusTypeWaitingDelivery = 3, //运输中
    // JDISVOrderListOrderStatusTypeCompleted = 5, //已完成
    // JDISVOrderListOrderStatusTypeCancelled = 6, //已取消
    // JDISVOrderListOrderStatusTypeWaitingSelfDelivery = 7, //等待自提
    // JDISVOrderListOrderStatusTypeWaitingReceipt = 8, //待发货
    let dom = null;
    // 取消中不展示倒计时
    if (type === "1" && leftTime >= 0 && !item?.orderBaseInfo?.cancelProcessing) {
      // 待付款
      dom = (
        <div className={styles.count_down_wrap}>
          {t("order.orderList.orderItems.toPay")}
          <CountDown
            endTime={leftTime}
            endHandler={() => {
              // 延迟两秒调用，防止中台状态未变更
              setTimeout(() => {
                window.scrollTo(0, 0);
                orderStore.setPageNum(1);
                orderStore.getOrderList({ page: "1", state: orderStore.orderType || "4096" });
              }, 2000);
            }}
            externalStyles={{ marginLeft: "8px" }}
          />
        </div>
      );
    } else {
      if (
        item?.orderExtInfoDTO.showLogisticsInfo &&
        (item?.logisticsInfoDTO?.expectDeliveryTimeValue || item?.logisticsInfoDTO?.stateDesc || item?.logisticsInfoDTO?.message)
      ) {
        dom = (
          <>
            {/* 优先展示多包裹信息，其次展示最新物流，最后是预计送达时间 */}
            {item?.logisticsInfoDTO?.shippedPackageNum + item?.logisticsInfoDTO?.deliveredPackageNum > 1 ? ( // 多包裹场景
              <div
                onClick={(e) => {
                  setCurOrderItem(item);
                  showTrackHandler(e, orderId);
                }}
              >
                {item?.logisticsInfoDTO?.message}
              </div>
            ) : item?.logisticsInfoDTO?.stateDesc ? (
              <div
                onClick={(e) => {
                  setCurOrderItem(item);
                  showTrackHandler(e, orderId);
                }}
                className={styles.logisticsWrap}
              >
                <span className={styles.mainNodeStyle}>{item?.logisticsInfoDTO?.stateDesc}</span>
                <span className={styles.subNodeStyle}>{item?.logisticsInfoDTO?.message}</span>
              </div>
            ) : item?.logisticsInfoDTO?.expectDeliveryTimeValue ? (
              <div
                onClick={(e) => {
                  setCurOrderItem(item);
                  showTrackHandler(e, orderId);
                }}
              >
                {item?.logisticsInfoDTO?.expectDeliveryTimeTitle}: {item?.logisticsInfoDTO?.expectDeliveryTimeValue}
              </div>
            ) : null}
          </>
        );
      } else if (item?.orderExtInfoDTO.showRefundInfo) {
        dom =
          type === "3" || type === "8" ? (
            <div>{item?.refundInfoDTO?.refundApplyTimeTextForPc}</div>
          ) : (
            <div
              onClick={(event) => {
                // 退款完成 弹出退款弹窗
                if (item?.refundInfoDTO?.refundAmount) {
                  event.stopPropagation();
                  setShowRefundModal(true);
                  setCurOrder(orderId);
                }
              }}
            >
              {item?.refundInfoDTO?.refundProgressText}
              {item?.refundInfoDTO?.refundApplyTime ? <span className={styles.sub_title_style}>{item?.refundInfoDTO?.refundApplyTime}</span> : null}
            </div>
          );
      } else if (item?.orderExtInfoDTO?.showAfsProgressInfo) {
        dom = (
          <div
            onClick={(event) => {
              if (item?.afterSaleProgressDTO?.jumpFlag !== "afsList") {
                event.stopPropagation();
                navigate(`/my-orders/refund?afsServiceId=${item?.afterSaleProgressDTO?.afsServiceId}&orderId=${orderId}`);
              }
            }}
          >
            {item?.afterSaleProgressDTO?.afterSaleTitle}
          </div>
        );
      } else if (item?.orderExtInfoDTO?.showCancelReason) {
        dom = item?.orderBaseInfo?.cancelReason ? (
          <div className={cn(styles.detail_desc_wrap, styles.detail_desc_wrap_separate)}>
            <div className={styles.timeout_cancel}>{item?.orderBaseInfo?.cancelReason}</div>
          </div>
        ) : null;
      }
    }
    return dom;
  };

  const showTrackHandler = (event: React.MouseEvent<HTMLDivElement>, orderId: string) => {
    event.stopPropagation();
    setCurOrder(orderId);
    setShowTrackModal(true);
  };

  const handleBtnClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    clickType: number,
    item: {
      buttons: ButtonsVo[];
      orderBaseInfo: OrderBaseInfoVo;
      wareInfoList: WareInfoListVo[];
      priceInfoItemDTO: PriceInfoItemVo;
      afterSaleProgressDTO: AfterSaleProgressVo;
      orderExtInfoDTO: OrderExtInfoVo;
      logisticsInfoDTO: LogisticsInfoVo;
      invoiceItemDTO: InvoiceListVo[];
    },
    index: number,
  ) => {
    const orderId = item.orderBaseInfo?.orderId || "";
    const wareList = item.wareInfoList;
    // 埋点相关
    const packageNum = `${(item?.logisticsInfoDTO?.shippedPackageNum || 0) + (item?.logisticsInfoDTO?.deliveredPackageNum || 0)}`;
    // JDISVOrderListOrderActionFloorModelButtonTypePay = 1, /**< 1:去支付 */
    // JDISVOrderListOrderActionFloorModelButtonTypeBuyAgain = 2, /**< 2:再次购买 */
    // JDISVOrderListOrderActionFloorModelButtonTypeInvite = 3, /**< 3:查看邀请（暂无使用） */
    // JDISVOrderListOrderActionFloorModelButtonTypeConfirmReceive = 5, /**< 5:确认收货 */
    // JDISVOrderListOrderActionFloorModelButtonTypeEvaluate = 6, /**< 6:评价晒单 */
    // JDISVOrderListOrderActionFloorModelButtonTypeLogistics = 7, /**< 7:查看物流 */
    // JDISVOrderListOrderActionFloorModelButtonTypeRestoreOrder = 8, /**< 8:还原订单 */
    // JDISVOrderListOrderActionFloorModelButtonTypeRemainderOrder = 11, /**< 11:我要催单 */
    // JDISVOrderListOrderActionFloorModelButtonTypeCompleteRemainder = 12, /**< 12:已催单 */
    // JDISVOrderListOrderActionFloorModelButtonTypeCheckInvoice = 14, /**< 14:查看发票 */
    // JDISVOrderListOrderActionFloorModelButtonTypeCancelOrder = 15, /**< 15:取消订单 */
    // JDISVOrderListOrderActionFloorModelButtonTypeApplyAfterSale = 16, /**< 16:申请售后 */
    // JDISVOrderListOrderActionFloorModelButtonTypeApplyInvoice = 17, /**< 17:申请开票 */
    // JDISVOrderListOrderActionFloorModelButtonTypeApplyRefund = 18, /**< 18:申请退款 */
    // JDISVOrderListOrderActionFloorModelButtonTypeDeleteOrder = 19, /**< 19:删除订单 */
    // JDISVOrderListOrderActionFloorModelButtonTypeShareFirend = 20,/**< 20:邀请好友 */
    // JDISVOrderListOrderActionFloorModelButtonTypeRefundDetail = 21, /**< 21:退款明细 */
    // JDISVOrderListOrderActionFloorModelButtonTypeRemainderDetail = 22, /**< 22:催单详情 */
    // JDISVOrderListOrderActionFloorModelButtonTypeForceDelete = 23, /**< 23:彻底删除(宜宾) */
    // JDISVOrderListOrderActionFloorModelButtonTypePayFirstMoney = 24, /**< 24:支付定金*/
    // JDISVOrderListOrderActionFloorModelButtonTypePayLastMoney = 25, /**< 25:支付尾款*/
    // JDISVOrderListOrderActionFloorModelButtonTypeModifyOrder  = 27, /**< 27:修改订单*/
    // JDISVOrderListOrderActionFloorModelButtonTypeDelayReceive = 29, /**< 29:延迟收货*/
    event.stopPropagation();

    if (clickType === 2) {
      // 再次购买
      // 埋点
      sendClickTrack.OrderList_BuyAgain({
        orderId,
        orderStatus: item?.orderBaseInfo?.orderStatusId as "1" | "3" | "8" | "6" | "5",
        tab_name: tabName,
        index,
        page: currentPage,
        packageNum,
      });
      // 数据处理
      const formatWareList = wareList.map((item) => {
        return {
          skuId: item.wareId,
          num: item.buyCount,
        };
      });
      navigate("/Cart", { state: { productList: formatWareList } });
    } else if (clickType === 19) {
      // 删除订单
      // 埋点
      sendClickTrack.OrderList_DeleteButton({
        orderId,
        orderStatus: item?.orderBaseInfo?.orderStatusId as "1" | "3" | "8" | "6" | "5",
        tab_name: tabName,
        index,
        page: currentPage,
        packageNum,
      });
      setShowConfirmModal(true);
    } else if (clickType === 1) {
      // 去支付
      // 埋点
      sendClickTrack.OrderList_PayNow({
        orderId,
        orderStatus: item?.orderBaseInfo?.orderStatusId as "1" | "3" | "8" | "6" | "5",
        tab_name: tabName,
        index,
        page: currentPage,
        packageNum,
      });
      setShowPayModal(true);
    } else if (clickType === 6) {
      // 跳转评价
      // 埋点
      sendClickTrack.OrderList_Review({
        orderId,
        orderStatus: item?.orderBaseInfo?.orderStatusId as "1" | "3" | "8" | "6" | "5",
        tab_name: tabName,
        index,
        page: currentPage,
        packageNum,
      });
      navigate(`/Review?orderId=${orderId}`);
    } else if (clickType === 7) {
      // 7:查看物流
      // 埋点
      sendClickTrack.OrderList_OrderTrack({
        orderId,
        orderStatus: item?.orderBaseInfo?.orderStatusId as "1" | "3" | "8" | "6" | "5",
        tab_name: tabName,
        index,
        page: currentPage,
        packageNum,
      });
      setShowTrackModal(true);
    } else if (clickType === 14) {
      // 查看发票
      // 埋点
      sendClickTrack.OrderList_InvoiceService({
        orderId,
        orderStatus: item?.orderBaseInfo?.orderStatusId as "1" | "3" | "8" | "6" | "5",
        tab_name: tabName,
        index,
        page: currentPage,
        packageNum,
      });
      setShowInvoiceModal(true);
    }
  };

  // 操作按钮展示
  const btnGroupRender = (
    item: {
      buttons: ButtonsVo[];
      orderBaseInfo: OrderBaseInfoVo;
      wareInfoList: WareInfoListVo[];
      priceInfoItemDTO: PriceInfoItemVo;
      afterSaleProgressDTO: AfterSaleProgressVo;
      orderExtInfoDTO: OrderExtInfoVo;
      logisticsInfoDTO: LogisticsInfoVo;
      invoiceItemDTO: InvoiceListVo[];
      orderTagContentVO: TagContentVo;
    },
    ind: number,
  ) => {
    // JDISVOrderListOrderActionFloorModelButtonTypePay = 1, /**< 1:去支付 */
    // JDISVOrderListOrderActionFloorModelButtonTypeBuyAgain = 2, /**< 2:再次购买 */
    // JDISVOrderListOrderActionFloorModelButtonTypeInvite = 3, /**< 3:查看邀请（暂无使用） */
    // JDISVOrderListOrderActionFloorModelButtonTypeConfirmReceive = 5, /**< 5:确认收货 */
    // JDISVOrderListOrderActionFloorModelButtonTypeEvaluate = 6, /**< 6:评价晒单 */
    // JDISVOrderListOrderActionFloorModelButtonTypeLogistics = 7, /**< 7:查看物流 */
    // JDISVOrderListOrderActionFloorModelButtonTypeRestoreOrder = 8, /**< 8:还原订单 */
    // JDISVOrderListOrderActionFloorModelButtonTypeRemainderOrder = 11, /**< 11:我要催单 */
    // JDISVOrderListOrderActionFloorModelButtonTypeCompleteRemainder = 12, /**< 12:已催单 */
    // JDISVOrderListOrderActionFloorModelButtonTypeCheckInvoice = 14, /**< 14:查看发票 */
    // JDISVOrderListOrderActionFloorModelButtonTypeCancelOrder = 15, /**< 15:取消订单 */
    // JDISVOrderListOrderActionFloorModelButtonTypeApplyAfterSale = 16, /**< 16:申请售后 */
    // JDISVOrderListOrderActionFloorModelButtonTypeApplyInvoice = 17, /**< 17:申请开票 */
    // JDISVOrderListOrderActionFloorModelButtonTypeApplyRefund = 18, /**< 18:申请退款 */
    // JDISVOrderListOrderActionFloorModelButtonTypeDeleteOrder = 19, /**< 19:删除订单 */
    // JDISVOrderListOrderActionFloorModelButtonTypeShareFirend = 20,/**< 20:邀请好友 */
    // JDISVOrderListOrderActionFloorModelButtonTypeRefundDetail = 21, /**< 21:退款明细 */
    // JDISVOrderListOrderActionFloorModelButtonTypeRemainderDetail = 22, /**< 22:催单详情 */
    // JDISVOrderListOrderActionFloorModelButtonTypeForceDelete = 23, /**< 23:彻底删除(宜宾) */
    // JDISVOrderListOrderActionFloorModelButtonTypePayFirstMoney = 24, /**< 24:支付定金*/
    // JDISVOrderListOrderActionFloorModelButtonTypePayLastMoney = 25, /**< 25:支付尾款*/
    // JDISVOrderListOrderActionFloorModelButtonTypeModifyOrder  = 27, /**< 27:修改订单*/
    // JDISVOrderListOrderActionFloorModelButtonTypeDelayReceive = 29, /**< 29:延迟收货*/
    const orderId = item.orderBaseInfo?.orderId || "";
    const btnList = item.buttons;
    let dom = null;
    if (btnList.length) {
      const showBtnList = btnList.sort((a, b) => b.sort - a.sort);
      dom = showBtnList.map((ele, index) => {
        // 埋点相关
        let buttonExpoName = "";
        if (ele.showLabelId === 2) {
          // 再次购买
          buttonExpoName = EXPOSURE_RECORD.OrderList_BuyAgainExpo;
        } else if (ele.showLabelId === 1) {
          // 去支付
          buttonExpoName = EXPOSURE_RECORD.OrderList_PayNowExpo;
        } else if (ele.showLabelId === 6) {
          // 跳转评价
          buttonExpoName = EXPOSURE_RECORD.OrderList_ReviewExpo;
        } else if (ele.showLabelId === 7) {
          // 查看物流
          buttonExpoName = EXPOSURE_RECORD.OrderList_OrderTrackExpo;
        } else if (ele.showLabelId === 19) {
          // 删除订单
          buttonExpoName = EXPOSURE_RECORD.OrderList_DeleteButtonExpo;
        } else if (ele.showLabelId === 14) {
          // 发票
          buttonExpoName = EXPOSURE_RECORD.OrderList_InvoiceServiceExpo;
        }
        // 埋点相关
        const packageNum = `${item?.logisticsInfoDTO?.shippedPackageNum + item?.logisticsInfoDTO?.deliveredPackageNum || 0}`;
        const buttonExpoParams = {
          orderId,
          orderStatus: item?.orderBaseInfo?.orderStatusId as "1" | "3" | "8" | "6" | "5",
          tab_name: tabName,
          index: ind,
          page: currentPage,
          packageNum,
        };
        return (
          <CommonButton
            key={ele.showLabelId}
            variant={ele.showLabelId === 1 || ele.showLabelId === 2 ? "contained" : "outlined"}
            style={index === 0 ? {} : { marginLeft: "8px" }}
            onClick={(e) => {
              setCurOrder(orderId);
              setCurOrderItem(item);
              setCurOrderIndex(ind);
              handleBtnClick(e, ele.showLabelId, item, ind);
            }}
            exptag={"exp|" + buttonExpoName}
            data-exptag-config={JSON.stringify({
              useClick: false,
              stay_duration: ele.showLabelId === 14 ? 0.3 : 0,
              repeated: false,
              area_rate: ele.showLabelId === 14 ? 0.5 : 0.01,
            })}
            data-exptag-json-param={JSON.stringify(buttonExpoParams)}
          >
            {ele.showLabel}
          </CommonButton>
        );
      });
    }
    return dom;
  };

  // 删除订单确认操作
  const handleDelConfirm = () => {
    // 埋点
    const packageNum = `${(curOrderItem?.logisticsInfoDTO?.shippedPackageNum || 0) + (curOrderItem?.logisticsInfoDTO?.deliveredPackageNum || 0)}`;
    sendClickTrack.OrderList_ComfirmDelete({
      orderId: curOrderItem?.orderBaseInfo?.orderId || "",
      orderStatus: curOrderItem?.orderBaseInfo?.orderStatusId as "1" | "3" | "8" | "6" | "5",
      tab_name: tabName,
      index: curOrderIndex,
      page: currentPage,
      packageNum,
    });
    setShowConfirmModal(false);
    handleDelHistory();
  };

  // 删除订单操作
  const handleDelHistory = async () => {
    const requestData: IOrderDetailReq = { orderId: String(curOrder) };
    const [err, delHistoryOrderData] = await to(delHistoryOrder(requestData));

    if (err) {
      UKToast.show({
        content: t("order.orderList.orderItems.deleteFailTips"), // 弹窗内容
        icon: "fail", // 可选：success, fail, loading, warn, info，影响图标
        duration: 2000, // 不写的话默认3000
      });
    } else {
      if (delHistoryOrderData.result) {
        // 刷新列表
        UKToast.show({
          content: t("order.orderList.orderItems.deleteSuccessTips"), // 弹窗内容
          icon: "success", // 可选：success, fail, loading, warn, info，影响图标
          duration: 2000, // 不写的话默认3000
        });
        setTimeout(() => {
          window.scrollTo(0, 0);
          orderStore.setPageNum(1);
          orderStore.getOrderList({ page: "1", state: orderStore.orderType || "4096" });
        }, 2000);
      } else {
        UKToast.show({
          content: delHistoryOrderData.updateShowText || t("order.orderList.orderItems.deleteFailTips"), // 弹窗内容
          icon: "fail", // 可选：success, fail, loading, warn, info，影响图标
        });
      }
    }
  };

  const pagesChangeHandler = (page: number) => {
    window.scrollTo(0, 0); // 滚动到顶部
    orderStore.setPageNum(page);
    orderStore.getOrderList({ page: String(page), state: orderStore.orderType || "4096" });
  };

  // 计算Order items
  const calcNum = (list: WareInfoListVo[]) => {
    return list.reduce((sum: number, item) => sum + Number(item.buyCount), 0);
  };

  // 异常页面
  if (orderStore?.error) {
    const pointParams = {
      expoParam: { tab_name: tabName },
      expoID: EXPOSURE_RECORD.OrderList_TryAgainExpo,
      refreshPoint: () => sendClickTrack.OrderList_TryAgain({ tab_name: tabName }),
    };
    return <ErrorPage pointParams={pointParams} />;
  }

  // 空页面
  if (!(orderList && orderList.length)) {
    return <EmptyPage />;
  }

  return (
    <div className={styles.wrap}>
      {orderList.map(
        (
          item: {
            buttons: ButtonsVo[];
            orderBaseInfo: OrderBaseInfoVo;
            wareInfoList: WareInfoListVo[];
            priceInfoItemDTO: PriceInfoItemVo;
            afterSaleProgressDTO: AfterSaleProgressVo;
            orderExtInfoDTO: OrderExtInfoVo;
            logisticsInfoDTO: LogisticsInfoVo;
            invoiceItemDTO: InvoiceListVo[];
            refundInfoDTO: RefundInfoVo;
            orderTagContentVO: TagContentVo;
          },
          index: number,
        ) => {
          const orderId = item.orderBaseInfo.orderId || "";
          const showProduct = item?.wareInfoList.length > 3 && !openStates[orderId] ? item?.wareInfoList.slice(0, 3) : item?.wareInfoList;

          const priceUnit = item?.priceInfoItemDTO.unit || "";
          const price = item?.priceInfoItemDTO.price || "";

          // 埋点相关
          const packageNum = `${(item?.logisticsInfoDTO?.shippedPackageNum || 0) + (item?.logisticsInfoDTO?.deliveredPackageNum || 0)}`;

          return (
            <div
              className={styles.order_item_wrap}
              key={index}
              onClick={() => jumpOrderDetail(index, item)}
              exptag={"exp|" + EXPOSURE_RECORD.OrderList_OrderDetailExpo}
              data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
              data-exptag-json-param={JSON.stringify({
                orderId,
                orderStatus: item?.orderBaseInfo?.orderStatusId as "1" | "3" | "8" | "6" | "5",
                tab_name: tabName,
                index,
                page: currentPage,
                packageNum,
              })}
            >
              {/* 头部展示 */}
              <div className={styles.order_item_title}>
                <div className={cn(styles.left_wrap, styles.center_style)}>
                  <span className={styles.order_status}>{item?.orderBaseInfo.orderStatusName}</span>
                  <div className={cn(styles.center_style, styles.order_info_wrap)}>
                    <span className={styles.title_label_style}>{t("order.orderList.orderItems.headerOrderId")}</span>
                    {orderId}
                  </div>
                  {item?.orderBaseInfo.paymentTime ? (
                    <div className={cn(styles.center_style, styles.order_info_wrap)}>
                      <span className={styles.title_label_style}>{t("order.orderList.orderItems.headerOrderTime")}</span>
                      {item?.orderBaseInfo.paymentTime}
                    </div>
                  ) : null}
                </div>
                <div className={cn(styles.right_wrap, styles.center_style)}>
                  <div className={cn(styles.center_style)}>
                    {/* 大于 1件则展示items */}
                    {item?.wareInfoList.length ? (
                      <span className={styles.title_label_style}>
                        {calcNum(item?.wareInfoList)} {t("order.orderList.orderItems.headerOrderItems")}
                      </span>
                    ) : null}
                    <JDIPrice
                      symbol={priceUnit}
                      price={price}
                      uiType={2}
                      containerStyle={{
                        fontSize: "16px",
                        color: "#1A1A1A",
                      }}
                    />
                  </div>
                  <div className={cn(styles.center_style, styles.order_info_wrap, styles.to_detail)}>
                    {t("order.orderList.orderItems.headerOrderdetail")}
                    <VectorDown className={styles.entry_ico} />
                  </div>
                </div>
              </div>
              {/* 烟酒年龄限制提示 */}
              {item?.orderTagContentVO && item?.orderTagContentVO?.tagSet?.includes("isAdultProduct") && (
                <div
                  className={styles.ageTips}
                  onClick={(event) => {
                    event.stopPropagation();
                    setCurOrderItem(item);
                    setShowAgeTipsModal(true);
                  }}
                >
                  <WarningIcon />
                  <div className={styles.ageTipsContent}>{item?.orderTagContentVO?.tagMainContentMap?.isAdultProduct?.title}</div>
                  <VectorDown className={styles.entry_ico} />
                </div>
              )}
              {/* 商品图片、价格、数量展示区域 */}
              {showProduct.map((ele, ind) => {
                const realPrice = ele.price || "";
                const originPrice = ele.originPrice || "";
                const showOriginPrice = originPrice && Number(originPrice) > Number(realPrice);
                return (
                  <div className={styles.product_wrap} key={ind}>
                    <div className={styles.left_product}>
                      <div className={styles.product_info}>
                        <div className={styles.product_img}>
                          <UKImg
                            urlExtend={{
                              imgKey: ele.imageShortUrl ? ele.imageShortUrl : "",
                              width: 120,
                              height: 120,
                            }}
                          />
                        </div>
                        <div className={styles.product_main_info}>
                          <div className={styles.productName}>
                            <div className={styles.tagsWrap}>
                              <ProductTag tagList={ele?.showTagList || []} />
                            </div>
                            <div className={styles.product_name_cont}>{ele.wareName}</div>
                          </div>
                          <div className={styles.product_desc}>{ele.propertyExt}</div>
                        </div>
                      </div>
                    </div>
                    <div className={styles.right_product}>
                      <div className={styles.price_wrap}>
                        {/* 到手价 */}
                        <div className={styles.real_price}>
                          <JDIPrice
                            symbol={priceUnit}
                            price={realPrice}
                            uiType={1}
                            containerStyle={{
                              fontSize: "12px",
                              lineHeight: "18px",
                            }}
                            integerStyle={{
                              fontSize: "18px",
                              lineHeight: "24px",
                              height: "24px",
                            }}
                          />
                        </div>
                        {/* 划线价 */}
                        {showOriginPrice ? (
                          <div className={styles.origin_price}>
                            <JDIPrice
                              symbol={priceUnit}
                              price={originPrice}
                              uiType={2}
                              containerStyle={{
                                fontSize: "12px",
                                lineHeight: "16px",
                                height: "16px",
                                display: "block",
                              }}
                              lineThrough
                            />
                          </div>
                        ) : null}
                      </div>
                      <div className={styles.product_num}>×{ele.buyCount}</div>
                    </div>
                  </div>
                );
              })}
              {/* 展开与收起功能，只有超过三个才展示 */}
              {item?.wareInfoList?.length > 3 ? (
                <div className={styles.view_more} onClick={(e) => toggleStatus(e, orderId)}>
                  {openStates[orderId] ? t("order.orderList.orderItems.retractText") : t("order.orderList.orderItems.viewMoreText")}
                  <VectorDown
                    className={cn(styles.more_ico, {
                      [styles.retract_ico]: openStates[orderId],
                    })}
                  />
                </div>
              ) : null}
              {/* 操作按钮栏展示 */}
              {orderInfoRender(item) || btnGroupRender(item, index) ? (
                <div className={styles.operate_wrap}>
                  <div className={styles.operateWrapLeft}>{orderInfoRender(item)}</div>
                  <div className={styles.btn_group_wrap}>{btnGroupRender(item, index)}</div>
                </div>
              ) : null}
            </div>
          );
        },
      )}
      {orderStore?.orderListInfo?.totalCount ? (
        <div className={styles.pagination_wrap}>
          <Pagination currentPage={currentPage} totalPages={Math.ceil(orderStore?.orderListInfo?.totalCount / 10)} handlePageChange={pagesChangeHandler} />
        </div>
      ) : null}
      {/* 删除二次确认弹窗 */}
      {showConfirmModal && (
        <ConfirmModal
          content={t("order.orderList.orderItems.deleteConfirmText")}
          confirmHandler={handleDelConfirm}
          closeHandler={() => {
            // 埋点
            sendClickTrack.OrderList_CancelDelete({
              orderId: curOrderItem?.orderBaseInfo?.orderId || "",
              orderStatus: curOrderItem?.orderBaseInfo?.orderStatusId as "1" | "3" | "8" | "6" | "5",
              tab_name: tabName,
              index: curOrderIndex,
              page: currentPage,
              packageNum: `${(curOrderItem?.logisticsInfoDTO?.shippedPackageNum || 0) + (curOrderItem?.logisticsInfoDTO?.deliveredPackageNum || 0)}`,
            });
            setShowConfirmModal(false);
          }}
          trackingPointParams={{
            exptag: "exp|" + EXPOSURE_RECORD.OrderList_DeletePopExpo,
            "data-exptag-config": '{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}',
            "data-exptag-json-param": JSON.stringify({
              orderId: curOrderItem?.orderBaseInfo?.orderId || "",
              orderStatus: curOrderItem?.orderBaseInfo?.orderStatusId as "1" | "3" | "8" | "6" | "5",
              tab_name: tabName,
              index: curOrderIndex,
              page: currentPage,
              packageNum: `${(curOrderItem?.logisticsInfoDTO?.shippedPackageNum || 0) + (curOrderItem?.logisticsInfoDTO?.deliveredPackageNum || 0)}`,
            }),
          }}
        />
      )}
      {/* 去支付弹框 */}
      {showPayModal && (
        <RepayOrderModal
          orderId={curOrder}
          open={showPayModal}
          onClose={() => {
            setShowPayModal(false);
          }}
          onFresh={() => {
            window.scrollTo(0, 0);
            // 需要回到all tab页下
            orderStore.setOrderType("4096");
            orderStore.setPageNum(1);
            orderStore.getOrderList({ page: "1", state: "4096" });
            // orderStore.getOrderList({ page: "1", state: orderStore.orderType || "4096" });
            updateNumber && updateNumber();
          }}
        />
      )}
      {/* 物流轨迹弹框 */}
      {showTrackModal && (
        <TrackModal
          isOpen={showTrackModal}
          closeHandler={() => setShowTrackModal(false)}
          orderId={curOrder}
          title={curOrderItem?.orderBaseInfo?.orderStatusName || ""}
          commonTrackParams={{
            orderId: curOrderItem?.orderBaseInfo?.orderId || "",
            orderStatus: curOrderItem?.orderBaseInfo?.orderStatusId as "1" | "3" | "8" | "6" | "5",
          }}
        />
      )}
      {/* 发票弹框 */}
      {showInvoiceModal && <InvoiceModal isOpen={showInvoiceModal} closeHandler={() => setShowInvoiceModal(false)} orderId={curOrder} />}
      {/* 退款明细弹框 */}
      {showRefundModal && (
        <RefundModal isOpen={showRefundModal} closeHandler={() => setShowRefundModal(false)} handleConfirmClick={() => {}} orderId={curOrder} />
      )}
      {/* 年龄提示弹框 */}
      {showAgeTipsModal && (
        <AgeTipsModal
          isOpen={showAgeTipsModal}
          closeHandler={() => setShowAgeTipsModal(false)}
          modalInfo={curOrderItem?.orderTagContentVO?.tagMainContentMap?.isAdultProduct}
        />
      )}
    </div>
  );
});

export default orderItems;
